/**
 * Plan download after ClickDimensions form success — property ID + REST grant (no plan URL in DOM).
 */
const STORAGE_PLAN_ID = 'heizijde_property_plan_id';
const STORAGE_PLAN_TOKEN = 'heizijde_property_plan_token';
const STORAGE_PLAN_SOURCE = 'heizijde_property_plan_source';
const STORAGE_PLAN_ATTACHMENT = 'heizijde_property_plan_attachment';
const STORAGE_INQUIRY_REF = 'heizijde_property_inquiry_ref';
const STORAGE_INQUIRY_PROJECT = 'heizijde_property_inquiry_project';
const STORAGE_INQUIRY_PROPERTY = 'heizijde_property_inquiry_property';

/** @type {number} */
let pendingPropertyId = 0;
/** @type {'property'|'building_unit'} */
let pendingSource = 'property';
/** @type {number} */
let pendingAttachmentId = 0;
let downloadInProgress = false;
/** @type {number} */
let completionPollTimer = 0;
const COMPLETION_POLL_MS = 400;
const CTA_CLASS = 'property-plan-download-cta';
const CTA_BUTTON_CLASS = 'property-plan-download-cta__button';

function getRestBase() {
	const fromInquiry = window.vtcPropertyInquiry?.restBase || window.heizijdePropertyInquiry?.restBase;
	if (fromInquiry) {
		return fromInquiry.endsWith('/') ? fromInquiry : `${fromInquiry}/`;
	}

	const modalRest = window.vtcModal?.restBase || window.heizijdeModal?.restBase;
	if (modalRest) {
		const base = modalRest;
		return base.endsWith('/') ? base : `${base}/`;
	}

	const apiLink = document.querySelector('link[rel="https://api.w.org/"]');
	if (apiLink?.href) {
		const root = apiLink.href.endsWith('/') ? apiLink.href : `${apiLink.href}/`;
		return `${root}heizijde/v1/`;
	}

	const { origin, pathname } = window.location;
	const siteMatch = pathname.match(/^(\/[^/]+\/)/);
	const siteBase = siteMatch ? siteMatch[1] : '/';
	return `${origin}${siteBase}wp-json/heizijde/v1/`;
}

function parsePositiveInt(raw) {
	const id = Number.parseInt(String(raw || ''), 10);
	return Number.isFinite(id) && id > 0 ? id : 0;
}

function readPropertyIdFromAnchor(anchor) {
	return parsePositiveInt(anchor.dataset?.propertyId);
}

/**
 * @returns {{ source: 'property'|'building_unit', id: number, attachmentId: number }|null}
 */
function readPlanTargetFromAnchor(anchor) {
	const attachmentId = parsePositiveInt(anchor.dataset?.attachmentId);
	const buildingId = parsePositiveInt(anchor.dataset?.buildingId);
	if (attachmentId && buildingId) {
		return { source: 'building_unit', id: buildingId, attachmentId };
	}

	const propertyId = readPropertyIdFromAnchor(anchor);
	if (propertyId) {
		return { source: 'property', id: propertyId, attachmentId: 0 };
	}

	return null;
}

function persistPlanTarget(target) {
	pendingPropertyId = target.id;
	pendingSource = target.source;
	pendingAttachmentId = target.attachmentId;
	try {
		sessionStorage.setItem(STORAGE_PLAN_ID, String(target.id));
		sessionStorage.setItem(STORAGE_PLAN_SOURCE, target.source);
		sessionStorage.setItem(STORAGE_PLAN_ATTACHMENT, String(target.attachmentId || 0));
		sessionStorage.removeItem(STORAGE_PLAN_TOKEN);
	} catch {
		/* private mode etc. */
	}
}

function readStoredPlanTarget() {
	if (pendingPropertyId > 0) {
		return {
			source: pendingSource,
			id: pendingPropertyId,
			attachmentId: pendingAttachmentId,
		};
	}

	try {
		const id = parsePositiveInt(sessionStorage.getItem(STORAGE_PLAN_ID));
		if (!id) {
			return null;
		}
		const source = sessionStorage.getItem(STORAGE_PLAN_SOURCE) === 'building_unit' ? 'building_unit' : 'property';
		const attachmentId = parsePositiveInt(sessionStorage.getItem(STORAGE_PLAN_ATTACHMENT));
		pendingPropertyId = id;
		pendingSource = source;
		pendingAttachmentId = attachmentId;
		return { source, id, attachmentId };
	} catch {
		return null;
	}
}

function stopCompletionPoll() {
	if (completionPollTimer) {
		clearInterval(completionPollTimer);
		completionPollTimer = 0;
	}
}

function startCompletionPoll() {
	stopCompletionPoll();

	if (!getPendingPropertyId()) {
		return;
	}

	completionPollTimer = window.setInterval(() => {
		const target = readStoredPlanTarget();
		if (!target) {
			stopCompletionPoll();
			return;
		}

		const iframe = findModalPlanIframe();
		if (!iframe || !isIframeFormCompleted(iframe)) {
			return;
		}

		stopCompletionPoll();
		showPlanDownloadButton(target);
	}, COMPLETION_POLL_MS);
}

function getPlanDownloadLabel() {
	return window.heizijdeModal?.planDownloadLabel || window.vtcModal?.planDownloadLabel || 'Plan downloaden';
}

function findActiveModalRoot() {
	return (
		document.querySelector('.modal-overlay.is-active, .modal-overlay.is-open, .modal-overlay[aria-hidden="false"]') ||
		document.querySelector('.heizijde-modal-preload-host:not([hidden])')
	);
}

function findModalFormShell() {
	const modalRoot = findActiveModalRoot();
	if (!modalRoot) {
		return null;
	}

	const shell = modalRoot.querySelector('.modal-event__form, .modal-body__form');
	return shell instanceof HTMLElement ? shell : null;
}

function removePlanDownloadButton() {
	document.querySelectorAll(`.${CTA_CLASS}`).forEach((element) => element.remove());
}

function showPlanDownloadButton(target) {
	if (!target?.id) {
		return;
	}

	const shell = findModalFormShell();
	if (!shell) {
		return;
	}

	removePlanDownloadButton();

	const cta = document.createElement('div');
	cta.className = CTA_CLASS;
	cta.setAttribute('aria-live', 'polite');

	const inner = document.createElement('div');
	inner.className = 'property-plan-download-cta__inner wp-block-buttons';

	const buttonWrap = document.createElement('div');
	buttonWrap.className = 'wp-block-button property-plan-download-cta__button-wrap';

	const button = document.createElement('button');
	button.type = 'button';
	button.className = `wp-block-button__link wp-element-button ${CTA_BUTTON_CLASS}`;
	button.textContent = getPlanDownloadLabel();
	button.addEventListener('click', () => {
		void handlePlanDownloadButtonClick(target, button);
	});

	buttonWrap.appendChild(button);
	inner.appendChild(buttonWrap);
	cta.appendChild(inner);
	shell.appendChild(cta);
}

async function handlePlanDownloadButtonClick(target, button) {
	if (downloadInProgress || !target?.id) {
		return;
	}

	button.setAttribute('aria-busy', 'true');

	const ok = await attemptPlanDownload(target);

	button.removeAttribute('aria-busy');

	if (ok) {
		removePlanDownloadButton();
	}
}

function revealPlanDownloadIfReady() {
	const target = readStoredPlanTarget();
	if (!target) {
		return;
	}

	const iframe = findModalPlanIframe();
	if (!iframe || !isIframeFormCompleted(iframe)) {
		return;
	}

	stopCompletionPoll();
	showPlanDownloadButton(target);
}

function storePendingPlanDownload(target) {
	removePlanDownloadButton();
	persistPlanTarget(target);
	startCompletionPoll();
}

function clearPendingPlanDownload() {
	if (downloadInProgress) {
		return;
	}
	stopCompletionPoll();
	removePlanDownloadButton();
	pendingPropertyId = 0;
	pendingSource = 'property';
	pendingAttachmentId = 0;
	try {
		sessionStorage.removeItem(STORAGE_PLAN_ID);
		sessionStorage.removeItem(STORAGE_PLAN_TOKEN);
		sessionStorage.removeItem(STORAGE_PLAN_SOURCE);
		sessionStorage.removeItem(STORAGE_PLAN_ATTACHMENT);
	} catch {
		/* ignore */
	}
}

function getPendingPropertyId() {
	const target = readStoredPlanTarget();
	return target?.id || 0;
}

async function requestPlanDownloadGrant(target) {
	if (!target?.id) {
		return '';
	}

	const url =
		target.source === 'building_unit'
			? `${getRestBase()}building/${target.id}/plan-download/grant`
			: `${getRestBase()}property/${target.id}/plan-download/grant`;

	const options = {
		method: 'POST',
		credentials: 'same-origin',
	};

	if (target.source === 'building_unit') {
		options.headers = { 'Content-Type': 'application/json' };
		options.body = JSON.stringify({ attachment_id: target.attachmentId });
	}

	try {
		const response = await fetch(url, options);
		if (!response.ok) {
			return '';
		}
		const data = await response.json();
		if (data?.token) {
			sessionStorage.setItem(STORAGE_PLAN_TOKEN, String(data.token));
			return String(data.token);
		}
	} catch {
		/* network error */
	}
	return '';
}

function buildPlanRedeemUrl(target, token, stream = false) {
	const params = new URLSearchParams({ token });
	if (stream) {
		params.set('stream', '1');
	}
	const path =
		target.source === 'building_unit'
			? `building/${target.id}/plan-download`
			: `property/${target.id}/plan-download`;
	return `${getRestBase()}${path}?${params.toString()}`;
}

function triggerBlobDownload(blob, filename) {
	const objectUrl = URL.createObjectURL(blob);
	const anchor = document.createElement('a');
	anchor.href = objectUrl;
	anchor.download = filename || 'plan.pdf';
	anchor.rel = 'noopener';
	anchor.style.display = 'none';
	document.body.appendChild(anchor);
	anchor.click();
	anchor.remove();
	window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
}

function triggerFileUrlDownload(url) {
	const iframe = document.createElement('iframe');
	iframe.setAttribute('aria-hidden', 'true');
	iframe.tabIndex = -1;
	iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden';
	iframe.src = url;
	document.body.appendChild(iframe);
	window.setTimeout(() => iframe.remove(), 120000);
}

async function downloadPlanFile(url, filename) {
	try {
		const fileResponse = await fetch(url, { credentials: 'same-origin' });
		if (fileResponse.ok) {
			const blob = await fileResponse.blob();
			if (blob.size > 0) {
				triggerBlobDownload(blob, filename);
				return true;
			}
		}
	} catch {
		/* fall through to iframe navigation */
	}

	triggerFileUrlDownload(url);
	return true;
}

/**
 * Redeem token via REST. Production returns JSON { url, filename }; newer plugin may stream binary when stream=1.
 *
 * @returns {Promise<{ ok: boolean, status?: number }>}
 */
async function redeemPlanDownload(target, token) {
	const jsonUrl = buildPlanRedeemUrl(target, token, false);

	try {
		const response = await fetch(jsonUrl, { credentials: 'same-origin' });
		const status = response.status;

		if (!response.ok) {
			return { ok: false, status };
		}

		const contentType = response.headers.get('content-type') || '';

		if (contentType.includes('application/json')) {
			const data = await response.json();
			if (!data?.url) {
				return { ok: false, status };
			}

			await downloadPlanFile(data.url, data.filename || 'plan.pdf');
			return { ok: true };
		}

		const blob = await response.blob();
		if (blob.size === 0) {
			return { ok: false, status };
		}

		triggerBlobDownload(blob, 'plan.pdf');
		return { ok: true };
	} catch {
		return { ok: false };
	}
}

async function attemptPlanDownload(target) {
	if (downloadInProgress || !target?.id) {
		return false;
	}

	downloadInProgress = true;

	try {
		let token = '';
		try {
			token = sessionStorage.getItem(STORAGE_PLAN_TOKEN) || '';
		} catch {
			token = '';
		}

		if (!token) {
			token = await requestPlanDownloadGrant(target);
		}

		if (!token) {
			return false;
		}

		let result = await redeemPlanDownload(target, token);

		if (!result.ok && result.status === 403) {
			token = await requestPlanDownloadGrant(target);
			if (token) {
				result = await redeemPlanDownload(target, token);
			}
		}

		if (!result.ok) {
			return false;
		}

		pendingPropertyId = 0;
		pendingSource = 'property';
		pendingAttachmentId = 0;
		try {
			sessionStorage.removeItem(STORAGE_PLAN_ID);
			sessionStorage.removeItem(STORAGE_PLAN_TOKEN);
			sessionStorage.removeItem(STORAGE_PLAN_SOURCE);
			sessionStorage.removeItem(STORAGE_PLAN_ATTACHMENT);
		} catch {
			/* ignore */
		}

		return true;
	} finally {
		downloadInProgress = false;
	}
}

function readDatasetValue(anchor, key) {
	const raw = anchor.dataset?.[key];
	return raw ? String(raw).trim() : '';
}

function storePlanInquiryReference(anchor) {
	const ref = readDatasetValue(anchor, 'propertyReference');
	const project = readDatasetValue(anchor, 'projectName');
	const property = readDatasetValue(anchor, 'propertyName');
	if (!ref && !project && !property) {
		return;
	}
	try {
		if (ref) {
			sessionStorage.setItem(STORAGE_INQUIRY_REF, ref);
		}
		if (project) {
			sessionStorage.setItem(STORAGE_INQUIRY_PROJECT, project);
		}
		if (property) {
			sessionStorage.setItem(STORAGE_INQUIRY_PROPERTY, property);
		}
	} catch {
		/* private mode etc. */
	}
}

function handlePlanDownloadClick(event) {
	const anchor = event.target.closest('a.js-property-plan-download');
	if (!anchor) {
		return;
	}

	const target = readPlanTargetFromAnchor(anchor);
	if (!target) {
		return;
	}

	storePlanInquiryReference(anchor);
	storePendingPlanDownload(target);
	void requestPlanDownloadGrant(target);
}

function isPlanDownloadFormContext(context) {
	return context === 'modal' || context === 'iframe';
}

async function handleFormSuccess(event) {
	const formContext = event.detail?.form_context;
	if (!isPlanDownloadFormContext(formContext)) {
		return;
	}

	revealPlanDownloadIfReady();
}

function findModalPlanIframe() {
	const modalRoot = findActiveModalRoot();

	if (!modalRoot) {
		return null;
	}

	const iframe = modalRoot.querySelector('.heizijde-cd-form__iframe');
	return iframe instanceof HTMLIFrameElement ? iframe : null;
}

function isIframeFormCompleted(iframe) {
	try {
		const doc = iframe.contentDocument;
		return Boolean(doc?.querySelector('.sd-completedpage'));
	} catch {
		return false;
	}
}

function retryDownloadIfFormAlreadyCompleted() {
	const target = readStoredPlanTarget();
	if (!target) {
		return;
	}

	startCompletionPoll();
	revealPlanDownloadIfReady();
}

function handleModalClosed() {
	window.setTimeout(clearPendingPlanDownload, 4000);
}

function init() {
	document.addEventListener('click', handlePlanDownloadClick, true);
	document.addEventListener('heizijde:clickdimensions-form-success', handleFormSuccess);
	document.addEventListener('modal:closed', handleModalClosed);
	document.addEventListener('modal:opened', retryDownloadIfFormAlreadyCompleted);
	document.addEventListener('heizijde:clickdimensions-iframe-ready', retryDownloadIfFormAlreadyCompleted);
}

init();
